import type { DeviceInfo } from "@musejs/muse";
import { EEG_BANDS, type EegBand } from "@musejs/signal";
import type { BandPowerReport } from "@musejs/features";
import type { QualityReport } from "@musejs/core";
import { BAND_GLYPH, BAND_TOKEN, type Dominant } from "./analysis.js";
import type { InspectReport } from "./inspect.js";

const BANDS = Object.keys(EEG_BANDS) as EegBand[];

export const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`Missing element #${id}`);
  return node as T;
};

/**
 * Groups digits with a thin space.
 *
 * `toLocaleString()` follows the browser's locale, which renders 2228 as
 * "2.228" in much of Europe — indistinguishable from a decimal in a table that
 * also holds rates like "64.1 Hz". A locale-independent separator cannot be
 * misread.
 */
function grouped(value: number): string {
  return value.toLocaleString("en-US").replaceAll(",", "\u2009");
}

function row(label: string, value: string): HTMLElement {
  const dt = document.createElement("dt");
  dt.textContent = label;
  const dd = document.createElement("dd");
  dd.textContent = value;
  const wrap = document.createElement("div");
  wrap.append(dt, dd);
  return wrap;
}

// ── Contact and artifacts ───────────────────────────────────────────────────

const FIT_LABEL: Readonly<Record<number, string>> = { 1: "good", 2: "usable", 4: "poor" };
const FIT_TONE: Readonly<Record<number, string>> = {
  1: "var(--good)",
  2: "var(--usable)",
  4: "var(--poor)",
};
const FIT_FILL: Readonly<Record<number, number>> = { 1: 100, 2: 60, 4: 20 };

export function renderQuality(report: QualityReport, scalp: readonly string[]): void {
  const list = el("fitList");
  list.replaceChildren(
    ...scalp
      .filter((name) => report.channels[name] !== undefined)
      .map((name) => {
        const channel = report.channels[name]!;
        const li = document.createElement("li");
        li.innerHTML = `<span>${name}</span><i></i><span>${FIT_LABEL[channel.fit] ?? "—"}</span>`;
        li.style.setProperty("--tone", FIT_TONE[channel.fit] ?? "var(--ink-faint)");
        li.style.setProperty("--fill", `${FIT_FILL[channel.fit] ?? 0}%`);
        return li;
      }),
  );
  el("wornValue").textContent =
    report.headbandOn === null ? "unknown" : report.headbandOn ? "yes" : "no";
  el("lossValue").textContent = `${(report.lossRate * 100).toFixed(2)}%`;
}

export function renderArtifacts(blink: boolean, jaw: boolean): void {
  for (const [id, active] of [
    ["blinkValue", blink],
    ["jawValue", jaw],
  ] as const) {
    const node = el(id);
    node.textContent = active ? "now" : "—";
    node.dataset["flag"] = String(active);
  }
}

// ── Bands ───────────────────────────────────────────────────────────────────

/**
 * One row per channel, five segments per row.
 *
 * A stacked bar rather than five separate ones: the five shares sum to 1, so
 * showing them as parts of a whole is the only reading that is true to the
 * number. Five independent bars would invite comparing lengths that cannot be
 * compared.
 */
export function renderBandMatrix(report: BandPowerReport, scalp: readonly string[]): void {
  const host = el("bandMatrix");
  const names = scalp.filter((name) => report.channels[name] !== undefined);
  if (names.length === 0) return;

  host.replaceChildren(
    ...names.map((name) => {
      const channel = report.channels[name]!;
      const wrap = document.createElement("div");
      wrap.className = "matrix__row";

      const label = document.createElement("b");
      label.textContent = name;

      const bar = document.createElement("div");
      bar.className = "matrix__bar";
      for (const band of BANDS) {
        const share = channel.relative[band];
        const segment = document.createElement("i");
        segment.style.width = `${share * 100}%`;
        segment.style.background = `var(${BAND_TOKEN[band]})`;
        segment.title = `${band} ${(share * 100).toFixed(1)}%`;
        bar.append(segment);
      }

      const figures = document.createElement("div");
      figures.className = "matrix__figures";
      for (const band of BANDS) {
        const cell = document.createElement("span");
        cell.innerHTML =
          `<i style="background: var(${BAND_TOKEN[band]})"></i>` +
          `${(channel.relative[band] * 100).toFixed(0)}%`;
        figures.append(cell);
      }

      wrap.append(label, bar, figures);
      return wrap;
    }),
  );
}

// ── Dominant frequency ──────────────────────────────────────────────────────

export function renderDominants(entries: ReadonlyMap<string, Dominant | null>): void {
  const host = el("dominantList");
  if (entries.size === 0) return;

  host.replaceChildren(
    ...[...entries].map(([name, dominant]) => {
      const card = document.createElement("div");
      card.className = "dominant";
      if (dominant === null) {
        card.innerHTML = `<b>${name}</b><em>—</em><span>not enough signal</span>`;
        return card;
      }
      // A peak that does not clear the background is reported as absent, not
      // as a number with a caveat attached. Most people read the number.
      if (!dominant.clear) {
        card.style.setProperty("--tone", "var(--ink-faint)");
        card.innerHTML =
          `<b>${name}</b>` +
          `<em class="dominant--none">none</em>` +
          `<span>no peak above the 1/f background</span>` +
          `<i class="dominant__sharp" style="--fill:0%"></i>` +
          `<u>slope 1/f<sup>${dominant.exponent.toFixed(1)}</sup></u>`;
        return card;
      }

      const tone = dominant.band === null ? "--ink-faint" : BAND_TOKEN[dominant.band];
      card.style.setProperty("--tone", `var(${tone})`);
      card.innerHTML =
        `<b>${name}</b>` +
        `<em>${dominant.hz.toFixed(1)}<small>Hz</small></em>` +
        `<span>${dominant.band ?? "outside the bands"}</span>` +
        // 12 dB above background is a very strong rhythm; the bar saturates there.
        `<i class="dominant__sharp" style="--fill:${Math.min(100, (dominant.prominenceDb / 12) * 100)}%"></i>` +
        `<u>+${dominant.prominenceDb.toFixed(1)} dB · slope 1/f<sup>${dominant.exponent.toFixed(1)}</sup></u>`;
      return card;
    }),
  );
}

export function renderBandLegend(): void {
  const list = el("bandLegend");
  list.replaceChildren(
    ...BANDS.map((band) => {
      const [low, high] = EEG_BANDS[band];
      const li = document.createElement("li");
      li.innerHTML =
        `<i style="background: var(${BAND_TOKEN[band]})"></i>` +
        `<b>${BAND_GLYPH[band]}</b><span>${band}</span><em>${low}–${high} Hz</em>`;
      return li;
    }),
  );
}

// ── Device ──────────────────────────────────────────────────────────────────

export function renderDevice(info: DeviceInfo, mtu: number | null, protocol: string): void {
  const named: [string, string | null][] = [
    ["Name", info.name],
    ["Model", protocol === "legacy" ? "pre-Athena" : "Muse S Athena (MS-03)"],
    ["Firmware", info.firmware],
    ["Hardware", info.hardware],
    ["Bootloader", info.bootloader],
    ["Build", info.buildNumber === null ? null : String(info.buildNumber)],
    ["Protocol version", info.protocolVersion === null ? null : String(info.protocolVersion)],
    ["Serial", info.serialNumber],
    ["MAC", info.macAddress],
    ["Battery", info.batteryPercent === null ? null : `${info.batteryPercent.toFixed(0)}%`],
    ["Preset (device says)", info.presetDecimal === null ? null : describePreset(info.presetDecimal)],
    ["Notification payload", mtu === null ? "not yet known" : `${mtu} B`],
    ["Wire protocol", protocol],
  ];

  const rows = named
    .filter(([, value]) => value !== null)
    .map(([label, value]) => row(label, value as string));
  el("deviceSpec").replaceChildren(...(rows.length > 0 ? rows : [emptyLine("Nothing heard yet.")]));

  const raw = Object.entries(info.raw);
  el("deviceRaw").replaceChildren(
    ...(raw.length > 0
      ? raw.map(([key, value]) => row(key, String(value)))
      : [emptyLine("No control replies yet.")]),
  );
}

/**
 * The status reply reports the preset in decimal while commands are hex.
 *
 * That mismatch is what made `p4129` look like a valid token when it is
 * actually a request for 0x4129 — so both forms are shown.
 */
function describePreset(decimal: number): string {
  return `${decimal} — token p${decimal.toString(16)}`;
}

function emptyLine(text: string): HTMLElement {
  const p = document.createElement("p");
  p.className = "panel__empty";
  p.textContent = text;
  return p;
}

// ── Inspect ─────────────────────────────────────────────────────────────────

export function renderInspect(report: InspectReport): void {
  const host = el("inspectResult");
  const wrap = document.createElement("div");
  wrap.className = "report";

  const head = document.createElement("dl");
  head.className = "spec";
  head.append(
    row("File", report.name),
    row("Device", `${report.device} (${report.model})`),
    row("Transport", report.transport),
    row("Notification payload", report.mtu),
    row("Preset requested", report.presetRequested),
    row("Duration", `${report.durationSeconds.toFixed(1)} s`),
    row("Notifications", grouped(report.frames)),
    row("Packets", grouped(report.packets)),
    row("Markers", String(report.markers)),
    row("Integrity", report.integrityOk ? "matches its digest" : "MISMATCH"),
    row("Anomalies", report.anomalies === 0 ? "none" : String(report.anomalies)),
    row(
      "Device clock",
      `${report.clockRateHz.toFixed(3)} ticks/s — ${report.clockFamily}`,
    ),
  );

  const signalTable = document.createElement("table");
  signalTable.className = "table";
  signalTable.innerHTML =
    "<thead><tr><th>Signal</th><th>Ch</th><th>Samples</th><th>Measured</th><th>Nominal</th><th>Dropped</th></tr></thead>";
  const body = document.createElement("tbody");
  for (const signal of report.signals) {
    const tr = document.createElement("tr");
    tr.innerHTML =
      `<td>${signal.signal}</td>` +
      `<td>${signal.channels}</td>` +
      `<td>${grouped(signal.samples)}</td>` +
      `<td>${signal.measuredRate.toFixed(1)} Hz</td>` +
      `<td>${signal.nominalRate} Hz</td>` +
      `<td${signal.dropped > 0 ? ' data-flag="true"' : ""}>${signal.dropped}</td>`;
    body.append(tr);
  }
  signalTable.append(body);

  const tagTable = document.createElement("table");
  tagTable.className = "table";
  tagTable.innerHTML = "<thead><tr><th>TAG</th><th>Sensor</th><th>Subpackets</th></tr></thead>";
  const tagBody = document.createElement("tbody");
  for (const tag of report.tags) {
    const tr = document.createElement("tr");
    tr.innerHTML =
      `<td>0x${tag.tag.toString(16).padStart(2, "0")}</td>` +
      `<td>${tag.label}</td>` +
      `<td>${grouped(tag.count)}</td>`;
    tagBody.append(tr);
  }
  tagTable.append(tagBody);

  wrap.append(head, heading("Signals"), signalTable, heading("Subpacket tags"), tagTable);
  host.replaceChildren(wrap);
}

function heading(text: string): HTMLElement {
  const h = document.createElement("h3");
  h.className = "report__heading";
  h.textContent = text;
  return h;
}

export function renderInspectError(message: string): void {
  const p = document.createElement("p");
  p.className = "notice";
  p.textContent = message;
  el("inspectResult").replaceChildren(p);
}
